import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import GlobalSearch from '../components/GlobalSearch';
import { FaBuilding } from 'react-icons/fa';

const API = 'https://buzzinguniverse.com/backend/api';

const UserCardSkeleton = () => (
    <div className="sr-card sr-card-skeleton">
        <div className="sr-card-left">
            <div className="sr-avatar-skeleton" />
        </div>
        <div className="sr-card-body">
            <div className="sr-skel-line sr-skel-name" />
            <div className="sr-skel-line sr-skel-sub" />
            <div className="sr-skel-line sr-skel-sub short" />
        </div>
    </div>
);

const ConnectBtn = ({ user, auth, onAction }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(user.friendship_status);
    const [direction, setDirection] = useState(user.direction);

    const handleConnect = async () => {
        if (!auth?.id) return;
        setLoading(true);
        try {
            await axios.post(`${API}/send-request`, {
                sender_id: auth.id,
                receiver_id: user.id,
            });
            setStatus('pending');
            setDirection('sent');
            onAction?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (!auth?.id || auth.id === user.id) return null;

    if (status === 'friend') {
        return (
            <Link to={`/member/${auth.id}/profile`} className="sr-btn sr-btn-msg" onClick={e => e.stopPropagation()}>
                <i className="fas fa-comment-alt" /> Message
            </Link>
        );
    }
    if (status === 'pending') {
        return (
            <button className="sr-btn sr-btn-pending" disabled>
                {direction === 'sent' ? 'Pending' : 'Respond'}
            </button>
        );
    }
    return (
        <button className="sr-btn sr-btn-connect" onClick={handleConnect} disabled={loading}>
            {loading ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-user-plus" /> Connect</>}
        </button>
    );
};

const UserCard = ({ user, auth }) => {
    const photo = user.photo
        ? `https://buzzinguniverse.com/backend/${user.photo}`
        : '/images/default.png';

    const location = [user.city, user.state].filter(Boolean).join(', ');

    return (
        <div className="sr-card">
            <div className="sr-card-left">
                <Link to={`/member/${user.id}/profile`}>
                    <img src={photo} alt={user.first_name} className="sr-avatar" />
                </Link>
            </div>
            <div className="sr-card-body">
                <Link to={`/member/${user.id}/profile`} className="sr-name">
                    {user.first_name} {user.last_name}
                </Link>
                {user.about && (
                    <p className="sr-about">
                        {user.about.length > 100 ? user.about.slice(0, 100) + '…' : user.about}
                    </p>
                )}
                {location && <p className="sr-location"><i className="fas fa-map-marker-alt" /> {location}</p>}
                <div className="sr-meta">
                    {user.mutual_count > 0 && (
                        <span className="sr-mutual">
                            <i className="fas fa-user-friends" /> {user.mutual_count} mutual connection{user.mutual_count !== 1 ? 's' : ''}
                        </span>
                    )}
                    {user.friend_count > 0 && (
                        <span className="sr-friends">
                            {user.friend_count} connection{user.friend_count !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>
            <div className="sr-card-actions">
                <ConnectBtn user={user} auth={auth} />
            </div>
        </div>
    );
};

const GroupCard = ({ group, isCompany }) => {
    const photo = group.profile_picture
        ? `https://buzzinguniverse.com/backend/${group.profile_picture}`
        : null;

    return (
        <div className="sr-card">
            <div className="sr-card-left">
                <Link to={`/groups/${group.id}`}>
                    {photo ? (
                        <img src={photo} alt={group.name} className="sr-avatar" />
                    ) : isCompany ? (
                        <div className="sr-avatar sr-avatar-placeholder">
                            <FaBuilding size={42} />
                        </div>
                    ) : (
                        <img src="/images/mystery-group.png" alt={group.name} className="sr-avatar" />
                    )}
                </Link>
            </div>
            <div className="sr-card-body">
                <Link to={`/groups/${group.id}`} className="sr-name">{group.name}</Link>
                {group.description && (
                    <p className="sr-about">
                        {group.description.length > 100 ? group.description.slice(0, 100) + '…' : group.description}
                    </p>
                )}
                {group.city && (
                    <p className="sr-location"><i className="fas fa-map-marker-alt" /> {group.city}</p>
                )}
                {group.privacy && (
                    <p className="sr-location" style={{ textTransform: 'capitalize' }}>
                        <i className="fas fa-lock" style={{ marginRight: 4 }} />{group.privacy}
                    </p>
                )}
            </div>
            <div className="sr-card-actions">
                <Link to={`/groups/${group.id}`} className="sr-btn sr-btn-connect">
                    View
                </Link>
            </div>
        </div>
    );
};

const SearchResults = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));

    const q = searchParams.get('q') || '';
    const type = searchParams.get('type') || 'people';

    // Sidebar counts
    const [counts, setCounts] = useState({ people: null, groups: null, companies: null, city: null });
    const isCity = type === 'city';

    // People state
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    // Groups / Companies state
    const [groups, setGroups] = useState([]);
    const [groupPage, setGroupPage] = useState(1);
    const [groupLastPage, setGroupLastPage] = useState(1);
    const [groupTotal, setGroupTotal] = useState(0);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupLoadingMore, setGroupLoadingMore] = useState(false);

    const fetchPeople = useCallback(async (kw, page, append = false, by = '') => {
        if (!kw) return;
        if (page === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await axios.get(`${API}/search/people`, {
                params: { keyword: kw, user_id: auth?.id || '', page, by },
            });
            const data = res.data?.data;
            if (data) {
                setUsers(prev => append ? [...prev, ...data.data] : data.data);
                setCurrentPage(data.current_page);
                setLastPage(data.last_page);
                setTotal(data.total);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [auth?.id]);

    const fetchGroups = useCallback(async (kw, groupType, page, append = false, by = '') => {
        if (!kw) return;
        if (page === 1) setGroupLoading(true);
        else setGroupLoadingMore(true);

        try {
            const res = await axios.get(`${API}/search/groups`, {
                params: { keyword: kw, type: groupType, page, by },
            });
            const data = res.data?.data;
            if (data) {
                setGroups(prev => append ? [...prev, ...data.data] : data.data);
                setGroupPage(data.current_page);
                setGroupLastPage(data.last_page);
                setGroupTotal(data.total);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setGroupLoading(false);
            setGroupLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (!q) {
            setCounts({ people: null, groups: null, companies: null, city: null });
            return;
        }
        axios.get(`${API}/search`, { params: { keyword: q } })
            .then(res => {
                const d = res.data?.data;
                if (d) setCounts({
                    people:    d.users_count    ?? null,
                    groups:    d.groups_count   ?? null,
                    companies: d.companies_count ?? null,
                    city:      d.city_count     ?? null,
                });
            })
            .catch(() => {});
    }, [q]);

    useEffect(() => {
        setUsers([]);
        setGroups([]);
        setTotal(0);
        setGroupTotal(0);
        setCurrentPage(1);
        setGroupPage(1);

        if (q) {
            if (type === 'people') fetchPeople(q, 1, false);
            else if (type === 'companies') fetchGroups(q, 'company', 1, false);
            else if (type === 'groups') fetchGroups(q, 'group', 1, false);
            else if (isCity) {
                // City tab shows both the people and the companies located in that city
                fetchPeople(q, 1, false, 'city');
                fetchGroups(q, 'company', 1, false, 'city');
            }
        }
    }, [q, type]);

    const handleShowMore = () => fetchPeople(q, currentPage + 1, true, isCity ? 'city' : '');
    const handleGroupShowMore = () => fetchGroups(q, type === 'groups' ? 'group' : 'company', groupPage + 1, true, isCity ? 'city' : '');

    const setTab = (t) => setSearchParams({ q, type: t });

    return (
        <div>
            <GlobalSearch />
            <div className="sr-page">
                <div className="container">
                    <div className="sr-layout">
                        {/* Left sidebar */}
                        <aside className="sr-sidebar">
                            <div className="sr-sidebar-card">
                                <h4 className="sr-sidebar-title">Search filters</h4>
                                <ul className="sr-filter-list">
                                    <li
                                        className={type === 'people' ? 'active' : ''}
                                        onClick={() => setTab('people')}
                                    >
                                        <span><i className="fas fa-users" /> People</span>
                                        {counts.people !== null && <span className="sr-filter-count">{counts.people}</span>}
                                    </li>
                                    <li
                                        className={type === 'companies' ? 'active' : ''}
                                        onClick={() => setTab('companies')}
                                    >
                                        <span><i className="fas fa-building" /> Companies</span>
                                        {counts.companies !== null && <span className="sr-filter-count">{counts.companies}</span>}
                                    </li>
                                    <li
                                        className={type === 'groups' ? 'active' : ''}
                                        onClick={() => setTab('groups')}
                                    >
                                        <span><i className="fas fa-layer-group" /> Groups</span>
                                        {counts.groups !== null && <span className="sr-filter-count">{counts.groups}</span>}
                                    </li>
                                    <li
                                        className={isCity ? 'active' : ''}
                                        onClick={() => setTab('city')}
                                    >
                                        <span><i className="fas fa-map-marker-alt" /> Search by City</span>
                                        {counts.city !== null && <span className="sr-filter-count">{counts.city}</span>}
                                    </li>
                                </ul>
                            </div>
                        </aside>

                        {/* Main content */}
                        <main className="sr-main">
                            {type === 'people' && (
                                <div className="sr-results-card">
                                    {q && !loading && (
                                        <p className="sr-count">
                                            {total > 0
                                                ? `${total} result${total !== 1 ? 's' : ''} for "${q}"`
                                                : `No results for "${q}"`}
                                        </p>
                                    )}

                                    {loading ? (
                                        [...Array(5)].map((_, i) => <UserCardSkeleton key={i} />)
                                    ) : (
                                        users.map(user => (
                                            <UserCard key={user.id} user={user} auth={auth} />
                                        ))
                                    )}

                                    {!loading && users.length > 0 && currentPage < lastPage && (
                                        <div className="sr-show-more">
                                            <button
                                                className="sr-btn-show-more"
                                                onClick={handleShowMore}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore
                                                    ? <><i className="fas fa-spinner fa-spin" /> Loading…</>
                                                    : 'Show more results'}
                                            </button>
                                        </div>
                                    )}

                                    {!loading && !q && (
                                        <div className="sr-empty">
                                            <i className="fas fa-search sr-empty-icon" />
                                            <p>Search for people by name</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(type === 'companies' || type === 'groups') && (
                                <div className="sr-results-card">
                                    {q && !groupLoading && (
                                        <p className="sr-count">
                                            {groupTotal > 0
                                                ? `${groupTotal} result${groupTotal !== 1 ? 's' : ''} for "${q}"`
                                                : `No results for "${q}"`}
                                        </p>
                                    )}

                                    {groupLoading ? (
                                        [...Array(5)].map((_, i) => <UserCardSkeleton key={i} />)
                                    ) : (
                                        groups.map(group => (
                                            <GroupCard key={group.id} group={group} isCompany={type === 'companies'} />
                                        ))
                                    )}

                                    {!groupLoading && groups.length > 0 && groupPage < groupLastPage && (
                                        <div className="sr-show-more">
                                            <button
                                                className="sr-btn-show-more"
                                                onClick={handleGroupShowMore}
                                                disabled={groupLoadingMore}
                                            >
                                                {groupLoadingMore
                                                    ? <><i className="fas fa-spinner fa-spin" /> Loading…</>
                                                    : 'Show more results'}
                                            </button>
                                        </div>
                                    )}

                                    {!groupLoading && !q && (
                                        <div className="sr-empty">
                                            <i className="fas fa-search sr-empty-icon" />
                                            <p>Search for {type === 'companies' ? 'companies' : 'groups'} by name</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isCity && (
                                <>
                                    <div className="sr-results-card">
                                        <p className="sr-count">
                                            <i className="fas fa-users" /> People in "{q}"
                                            {!loading && ` (${total})`}
                                        </p>

                                        {loading ? (
                                            [...Array(3)].map((_, i) => <UserCardSkeleton key={i} />)
                                        ) : users.length ? (
                                            users.map(user => (
                                                <UserCard key={user.id} user={user} auth={auth} />
                                            ))
                                        ) : (
                                            q && <div className="sr-empty"><p>No people found in this city</p></div>
                                        )}

                                        {!loading && users.length > 0 && currentPage < lastPage && (
                                            <div className="sr-show-more">
                                                <button className="sr-btn-show-more" onClick={handleShowMore} disabled={loadingMore}>
                                                    {loadingMore
                                                        ? <><i className="fas fa-spinner fa-spin" /> Loading…</>
                                                        : 'Show more people'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="sr-results-card" style={{ marginTop: 20 }}>
                                        <p className="sr-count">
                                            <i className="fas fa-building" /> Companies in "{q}"
                                            {!groupLoading && ` (${groupTotal})`}
                                        </p>

                                        {groupLoading ? (
                                            [...Array(3)].map((_, i) => <UserCardSkeleton key={i} />)
                                        ) : groups.length ? (
                                            groups.map(group => (
                                                <GroupCard key={group.id} group={group} isCompany />
                                            ))
                                        ) : (
                                            q && <div className="sr-empty"><p>No companies found in this city</p></div>
                                        )}

                                        {!groupLoading && groups.length > 0 && groupPage < groupLastPage && (
                                            <div className="sr-show-more">
                                                <button className="sr-btn-show-more" onClick={handleGroupShowMore} disabled={groupLoadingMore}>
                                                    {groupLoadingMore
                                                        ? <><i className="fas fa-spinner fa-spin" /> Loading…</>
                                                        : 'Show more companies'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {!q && (
                                        <div className="sr-empty">
                                            <i className="fas fa-search sr-empty-icon" />
                                            <p>Type a city name to find people and companies there</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchResults;
